import React, { useState, useEffect } from "react";
import { Button, Drawer, message } from "antd";
import { MenuOutlined, CloseOutlined, UserOutlined, LogoutOutlined } from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import LoginModal from "../auth/LoginModal";
import "./Header.css";

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [loginModalVisible, setLoginModalVisible] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading, logout } = useAuth();

  // Navigation items
  // HIDE
  const navigation = [
    { name: "Search", href: "/app/search", key: "search" },
    { name: "Box", href: "/app/box", key: "box" },
    { name: "Upload", href: "/app/upload", key: "upload" },
    { name: "Agent", href: "/app/agent", key: "agent" },
    { name: "Square", href: "/app/square", key: "square" },
    // { name: "ScienceOG", href: "/app/science-og", key: "science-og" },
    { name: "Settings", href: "/app/settings", key: "settings" },
    // { name: "Press (Building)", href: "/app/press", key: "press" },
    { name: "Foundation", href: "https://foundation.scai.sh/", external: true },
    // { name: "Community", href: "https://t.me/+AMy9MvWuVqhlNDY1", external: true },
  ];

  // 判断滚动
  const [isScroll, setIsScroll] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScroll(window.scrollY > 50);
    };

    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Set active section based on current path
  useEffect(() => {
    const path = location.pathname;
    if (path.includes("/app/search")) {
      setActiveSection("search");
    } else if (path.includes("/app/box")) {
      setActiveSection("box");
    } else if (path.includes("/app/upload")) {
      setActiveSection("upload");
    } else if (path.includes("/app/agent")) {
      setActiveSection("agent");
    } else if (path.includes("/app/square")) {
      setActiveSection("square");
    } else if (path.includes("/app/science-og")) {
      setActiveSection("science-og");
    } else if (path.includes("/app/settings")) {
      setActiveSection("settings");
    } else if (path.includes("/app/press")) {
      setActiveSection("press");
    } else if (path.includes("/app/tools")) {
      setActiveSection("tools");
    } else {
      setActiveSection("");
    }
  }, [location.pathname]);

  // Handle navigation click
  const handleNavClick = (item) => {
    setMobileMenuOpen(false);

    if (item.external) {
      window.open(item.href, "_blank", "noopener noreferrer");
    } else {
      navigate(item.href);
    }
  };

  // Handle login - show login modal
  const handleLogin = () => {
    setLoginModalVisible(true);
  };

  // Handle login success
  const handleLoginSuccess = () => {
    setLoginModalVisible(false);
    message.success("登录成功！");
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      message.success("Logged out");
      navigate("/app/search");
    } catch (error) {
      console.error("Logout failed:", error);
      message.error("Logout failed");
    }
  };



  return (
    <>
      <header className={`unified-header ${isScroll ? "unified-header-next" : ""}`}>
        <nav className="header-nav">
          <div className="header-content">
            {/* Logo */}
            <div className="header-logo">
              <div className="logo-container" onClick={() => navigate("/app/search")} style={{ cursor: "pointer" }}>
                <img src="/rocket-icon.png" alt="SCAI Logo" className="logo-image" />
                <span className="logo-text">SCAI</span>
              </div>

            </div>

            {/* Desktop Navigation - Centered */}
            {!isMobile && (
              <div className="desktop-nav-center">
                <div className="nav-links">
                  {navigation.map((item) => (
                    <button key={item.key || item.name} onClick={() => handleNavClick(item)} className={`nav-link ${activeSection === item.key ? "active" : ""}`}>
                      {item.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Right side - Wallet Button */}
            {!isMobile && (
              <div className="header-right">
                {!isAuthenticated ? (
                  <Button type="text" className="login-btn" onClick={handleLogin} loading={isLoading} icon={<UserOutlined />}>
                    Sign In
                  </Button>
                ) : (
                  <>
                    <Button type="text" className="login-btn" onClick={() => navigate('/app/settings?tab=account')} icon={<UserOutlined />}>
                      Profile
                    </Button>
                    <Button
                      onClick={handleLogout}
                      icon={<LogoutOutlined />}
                      style={{
                        marginLeft: 8,
                        color: '#000',
                        border: '1px solid #fff',
                        backgroundColor: '#fff'
                      }}
                    >
                      Log out
                    </Button>
                  </>
                )}
              </div>
            )}

            {/* Mobile menu button */}
            {isMobile && (
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="mobile-menu-button" aria-expanded={mobileMenuOpen}>
                <span className="sr-only">Open main menu</span>
                {mobileMenuOpen ? <CloseOutlined className="menu-icon" /> : <MenuOutlined className="menu-icon" />}
              </button>
            )}
          </div>

          {/* Mobile menu */}
          <Drawer title="Navigation" placement="left" onClose={() => setMobileMenuOpen(false)} open={mobileMenuOpen} className="mobile-drawer">
            <div className="mobile-nav-links">
              {navigation.map((item) => (
                <Button key={item.key || item.name} type="text" block onClick={() => handleNavClick(item)} className={`mobile-nav-link ${activeSection === item.key ? "active" : ""}`}>
                  {item.name}
                </Button>
              ))}

              {/* Mobile Wallet Button */}
              <div style={{ marginTop: "16px" }}>
                {!isAuthenticated ? (
                  <Button type="text" block icon={<UserOutlined />} className="mobile-nav-link" onClick={handleLogin} loading={isLoading}>
                    Sign In
                  </Button>
                ) : (
                  <>
                    <Button type="text" block className="mobile-nav-link" icon={<UserOutlined />} onClick={() => { setMobileMenuOpen(false); navigate('/app/settings?tab=account'); }}>
                      Profile
                    </Button>
                    <Button
                      block
                      className="mobile-nav-link"
                      icon={<LogoutOutlined />}
                      onClick={() => { setMobileMenuOpen(false); handleLogout(); }}
                      style={{
                        color: '#000',
                        border: '1px solid #fff',
                        backgroundColor: 'transparent'
                      }}
                    >
                      Log out
                    </Button>
                  </>
                )}
              </div>
            </div>
          </Drawer>
        </nav>
      </header>



      {/* Login Modal */}
      <LoginModal
        visible={loginModalVisible}
        onClose={() => setLoginModalVisible(false)}
        onSuccess={handleLoginSuccess}
      />
    </>
  );
};

export default Header;
